import { FC, useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { ProgramPayPlanDto } from "../../../../dtos/program-pay-plan-dto";
import { useApiGet } from "../../../../hooks";
import { isAPITotallyComplete } from "../../../../utilities/apiFunctions";
import {
  conditionHasValue,
  isEmptyValue,
} from "../../../../utilities/conditionalSupportFunctions";
import { Col, Input, Row, Select, Switch } from "../../../TrueUI";
import { rowWithNoMarginNorGutter } from "../../../TrueUI/Grids/Row";
import { BindInstructionsInfoAtom } from "./PolicyQuoteBindInstructionsAtoms";
import { PolicyQuoteBindInstructionsFormProps } from "./PolicyQuoteBindInstructionsTypes";
import {
  calculateBilledFeesByProrate,
  calculateBilledPremiumByProrate,
  calculatedBilledDepositAmount,
  colWithHorizontalGutterOnly,
  disabledProrateTerm,
  getBillingMethod,
  getCommonPropsForDepositRate,
  getDepositTypeByOverride,
  getLongevityThreshold,
  getPaymentPlanOptionAsPolicyPlan,
  getProgramPayPlanOptions,
  getStatesWithRatings,
  InstallmentBillingMethod,
  isDepositOverride,
  paymentPlanDepositTypeOptions,
} from "./PolicyQuoteBindInstructionsUtils";

const PolicyQuoteBindInstructionsForm: FC<
  PolicyQuoteBindInstructionsFormProps
> = ({ readOnly }) => {
  const [bindInstructionsInfo, setBindInstructionsInfo] = useRecoilState(
    BindInstructionsInfoAtom
  );
  const [paymentPlans, setPaymentPlans] = useState<ProgramPayPlanDto[]>([]);

  const effectiveDate = bindInstructionsInfo?.effectiveDate ?? null;
  const expirationDate = bindInstructionsInfo?.expirationDate ?? null;

  const { responseGet, dispatchGet } = useApiGet<ProgramPayPlanDto[]>(
    `api/QuotePolicy/GetProgramPayPlan?billedPremiumAmount=${
      bindInstructionsInfo?.billedPremium
    }&longevityThreshold=${getLongevityThreshold(
      bindInstructionsInfo?.firstPolicyDate,
      effectiveDate
    )}`
  );

  const onChangeProrateToTerm = (prorate: boolean) => {
    const statesWithRatings = getStatesWithRatings(
      bindInstructionsInfo?.states ?? []
    );

    const billedPremium = calculateBilledPremiumByProrate(
      prorate,
      statesWithRatings,
      effectiveDate,
      expirationDate
    );

    const billedFees = calculateBilledFeesByProrate(
      prorate,
      statesWithRatings,
      effectiveDate,
      expirationDate
    );

    const {
      billedDepositAmount,
      installmentPayment,
      nonEarningAmount,
      bindDiscountAmount,
    } = calculatedBilledDepositAmount(
      bindInstructionsInfo?.payPlan,
      billedPremium,
      bindInstructionsInfo?.depositType
    );

    setBindInstructionsInfo({
      ...bindInstructionsInfo,
      nonEarningAmount,
      installmentPayment,
      billedDepositAmount,
      bindDiscountAmount,
      prorateToTerm: prorate,
      billedPremium: billedPremium,
      billedFees: billedFees,
      dispatchTable: true,
      shouldReGenerateInvoices: true,
    });
  };

  const onChangePaymentPlan = (paymentPlanSelected: number) => {
    if (paymentPlanSelected !== bindInstructionsInfo?.payPlan?.payPlanID) {
      const paymentOption = getPaymentPlanOptionAsPolicyPlan(
        paymentPlans ?? [],
        paymentPlanSelected
      );

      const depositOverride = paymentOption.depositOverride === 1;

      const depositType = getDepositTypeByOverride(
        depositOverride,
        bindInstructionsInfo?.depositTypeForDepositOverride,
        paymentOption?.depositType
      );

      const {
        billedDepositAmount,
        installmentPayment,
        nonEarningAmount,
        bindDiscountAmount,
      } = calculatedBilledDepositAmount(
        paymentOption,
        bindInstructionsInfo?.billedPremium ?? 0,
        depositType
      );

      setBindInstructionsInfo({
        ...bindInstructionsInfo,
        nonEarningAmount,
        installmentPayment,
        billedDepositAmount,
        bindDiscountAmount,
        billingMethod: getBillingMethod(paymentOption?.planType),
        depositType: depositType,
        depositRate: paymentOption?.depositAmount,
        billByLocation: isEmptyValue(nonEarningAmount)
          ? bindInstructionsInfo?.billByLocation
          : false,
        payPlan: paymentOption,
        dispatchTable: true,
        shouldReGenerateInvoices: true,
      });
    }
  };

  const onChangeDepositTypeSelect = (depositType: string) => {
    const paymentOption = bindInstructionsInfo?.payPlan;

    const {
      billedDepositAmount,
      installmentPayment,
      nonEarningAmount,
      bindDiscountAmount,
    } = calculatedBilledDepositAmount(
      paymentOption,
      bindInstructionsInfo?.billedPremium ?? 0,
      depositType
    );

    setBindInstructionsInfo({
      ...bindInstructionsInfo,
      nonEarningAmount,
      installmentPayment,
      billedDepositAmount: billedDepositAmount,
      bindDiscountAmount,
      depositType: depositType,
      billByLocation: false,
      depositTypeForDepositOverride: depositType,
      dispatchTable: true,
      shouldReGenerateInvoices: true,
    });
  };

  const onChangeDepositRateInput = (depositRate: number) => {
    const paymentOption = bindInstructionsInfo?.payPlan;

    const newPayPlan = {
      ...paymentOption,
      depositRate: depositRate,
      depositAmount: depositRate,
      numberOfPayment: paymentOption?.numberOfPayment,
      allEqual: paymentOption?.allEqual ?? false,
      depositType: bindInstructionsInfo?.depositType,
    };

    const {
      billedDepositAmount,
      installmentPayment,
      nonEarningAmount,
      bindDiscountAmount,
    } = calculatedBilledDepositAmount(
      newPayPlan,
      bindInstructionsInfo?.billedPremium ?? 0,
      bindInstructionsInfo?.depositType
    );

    setBindInstructionsInfo({
      ...bindInstructionsInfo,
      nonEarningAmount,
      installmentPayment,
      billedDepositAmount: billedDepositAmount,
      bindDiscountAmount,
      depositRate: depositRate,
      payPlan: newPayPlan,
      shouldReGenerateInvoices: true,
      depositRateHasChanged: true,
    });
  };

  const onBlurDepositRateInput = () => {
    if (bindInstructionsInfo?.depositRateHasChanged === true) {
      setBindInstructionsInfo({
        ...bindInstructionsInfo,
        dispatchTable: true,
        depositRateHasChanged: false,
      });
    }
  };

  const onChangeBillByLocationCheckbox = (isChecked: boolean) => {
    setBindInstructionsInfo({
      ...bindInstructionsInfo,
      billByLocation: isChecked,
      dispatchTable: true,
      shouldReGenerateInvoices: true,
    });
  };

  useEffect(() => {
    if (isAPITotallyComplete(responseGet)) {
      setPaymentPlans(responseGet?.axiosResponse?.data ?? []);
    }
  }, [responseGet]);

  useEffect(() => {
    if (
      conditionHasValue(bindInstructionsInfo?.billedPremium) &&
      conditionHasValue(bindInstructionsInfo?.firstPolicyDate) &&
      conditionHasValue(effectiveDate)
    ) {
      dispatchGet();
    }
  }, [
    bindInstructionsInfo?.billedPremium,
    bindInstructionsInfo?.firstPolicyDate,
    effectiveDate,
  ]);

  return (
    <div>
      <Row
        {...rowWithNoMarginNorGutter}
        horizontalAlign={"flex-start"}
        numberOfColumns={24}
      >
        <Col
          {...colWithHorizontalGutterOnly}
          breakpoints={{ md: 4, lg: 4, xl: 4 }}
        >
          <Input
            id="id-total-premium-input"
            name="nameTotalPremiumInput"
            label="Total Premium"
            labelFontType="BOLD_CAPTION"
            value={bindInstructionsInfo?.totalPremium}
            align="right"
            labelAlign="end"
            readOnly
            inputWidth="110px"
          />
        </Col>
        <Col
          {...colWithHorizontalGutterOnly}
          breakpoints={{ md: 5, lg: 5, xl: 5 }}
        >
          <Input
            id="id-total-fees-input"
            name="totalFeesInput"
            label="Total Fees, Surcharges and Taxes"
            labelFontType="BOLD_CAPTION"
            value={bindInstructionsInfo?.totalFees}
            align="right"
            labelAlign="end"
            readOnly
            inputWidth="160px"
          />
        </Col>
        <Col
          {...colWithHorizontalGutterOnly}
          breakpoints={{ md: 5, lg: 5, xl: 5 }}
        >
          <Switch
            id="id-prorate-to-term-input"
            name="prorateToTermInput"
            label="Prorate to Term?"
            labelFontType="BODY"
            labelPlacement="start"
            isChecked={bindInstructionsInfo?.prorateToTerm}
            control={"checkbox"}
            onChangeIsChecked={onChangeProrateToTerm}
            readOnly={
              readOnly || disabledProrateTerm(effectiveDate, expirationDate)
            }
          />
        </Col>
        <Col
          {...colWithHorizontalGutterOnly}
          breakpoints={{ md: 4, lg: 4, xl: 4 }}
        >
          <Input
            id="id-total-billed-premium-input"
            name="totalBilledPremiumInput"
            label="Billed Premium"
            labelFontType="BOLD_CAPTION"
            value={bindInstructionsInfo?.billedPremium?.toLocaleString()}
            align="right"
            labelAlign="end"
            readOnly
            inputWidth="110px"
          />
        </Col>
        <Col
          {...colWithHorizontalGutterOnly}
          breakpoints={{ md: 5, lg: 5, xl: 5 }}
        >
          <Input
            id="id-total-billed-fees-input"
            name="totalBilledFeesInput"
            label="Billed Fees, Surcharges and Taxes"
            labelFontType="BOLD_CAPTION"
            value={bindInstructionsInfo?.billedFees ?? 0}
            align="right"
            labelAlign="end"
            readOnly
            inputWidth="160px"
          />
        </Col>
      </Row>
      <Row>
        <Col
          {...colWithHorizontalGutterOnly}
          breakpoints={{ md: 6, lg: 6, xl: 6 }}
        >
          <Select
            id="id-payment-plan-input"
            name="paymentPlanInput"
            label="Payment Plan"
            labelFontType="BOLD_CAPTION"
            inputFontType="BODY"
            variant="filled"
            value={bindInstructionsInfo?.payPlan?.payPlanID}
            options={getProgramPayPlanOptions(paymentPlans ?? [])}
            onChange={onChangePaymentPlan}
            firstOptionAsDefault={false}
            readOnly={readOnly}
          />
        </Col>
        <Col
          {...colWithHorizontalGutterOnly}
          breakpoints={{ md: 3, lg: 3, xl: 3 }}
        >
          <Input
            id="id-billing-method-input"
            name="billingMethodInput"
            label="Billing Method"
            labelFontType="BOLD_CAPTION"
            value={bindInstructionsInfo?.billingMethod}
            readOnly
            align="right"
            labelAlign="end"
          />
        </Col>
        <Col
          {...colWithHorizontalGutterOnly}
          breakpoints={{ md: 3, lg: 3, xl: 3 }}
        >
          {isDepositOverride(
            bindInstructionsInfo?.payPlan?.payPlanID ?? -1,
            paymentPlans ?? []
          ) ? (
            <Select
              id="id-deposit-type-input"
              name="depositTypeInput"
              label="Deposit Type"
              labelFontType="BOLD_CAPTION"
              inputFontType="BODY"
              variant="filled"
              value={bindInstructionsInfo?.depositType}
              options={paymentPlanDepositTypeOptions}
              onChange={onChangeDepositTypeSelect}
              readOnly={readOnly}
            />
          ) : (
            <Input
              id="id-deposit-type-input"
              name="depositTypeInput"
              label="Deposit Type"
              labelFontType="BOLD_CAPTION"
              value={bindInstructionsInfo?.depositType}
              readOnly
            />
          )}
        </Col>
        <Col
          {...colWithHorizontalGutterOnly}
          breakpoints={{ md: 3, lg: 3, xl: 3 }}
        >
          <Input
            id="id-deposit-rate-input"
            name="depositRateInput"
            label="Deposit Rate"
            labelFontType="BOLD_CAPTION"
            prefix=""
            value={bindInstructionsInfo?.depositRate}
            type="fixedCurrency"
            {...getCommonPropsForDepositRate(
              bindInstructionsInfo?.payPlan?.payPlanID,
              paymentPlans,
              readOnly
            )}
            onChangeRawValue={onChangeDepositRateInput}
            onBlur={onBlurDepositRateInput}
          />
        </Col>
        <Col
          {...colWithHorizontalGutterOnly}
          breakpoints={{ md: 5, lg: 5, xl: 5 }}
        >
          <Input
            id="id-billed-deposit-amount-input"
            name="billedDepositAmountInput"
            label="Billed Deposit Amount"
            labelFontType="BOLD_CAPTION"
            prefix=""
            value={bindInstructionsInfo?.billedDepositAmount}
            type="fixedCurrency"
            align="right"
            labelAlign="end"
            readOnly
            inputWidth="110px"
          />
        </Col>
        <Col
          {...colWithHorizontalGutterOnly}
          breakpoints={{ md: 4, lg: 4, xl: 4 }}
        >
          <Input
            id="id-number-payments-input"
            name="numberPaymentsInput"
            label="Number of Payments"
            labelFontType="BOLD_CAPTION"
            value={bindInstructionsInfo?.payPlan?.numberOfPayment}
            type="number"
            readOnly
          />
        </Col>
        <Col
          {...colWithHorizontalGutterOnly}
          breakpoints={{ md: 3, lg: 3, xl: 3 }}
        >
          {bindInstructionsInfo?.billingMethod === InstallmentBillingMethod &&
            bindInstructionsInfo.nonEarningAmount === null && (
              <Switch
                id="id-bill-by-location-input"
                name="billByLocationInput"
                label="Bill by Location"
                labelFontType="BODY"
                labelPlacement="end"
                isChecked={bindInstructionsInfo.billByLocation ?? false}
                control={"checkbox"}
                onChangeIsChecked={onChangeBillByLocationCheckbox}
                readOnly={readOnly}
              />
            )}
        </Col>
      </Row>
    </div>
  );
};

export default PolicyQuoteBindInstructionsForm;
